import React, { useState, useEffect, useMemo } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Title,
  Legend,
} from "chart.js";
import moment from "moment-jalaali";
import { apiFetch } from "../../api/client";

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Title,
  Legend
);

const Chart = ({ currencies, coins }) => {
  const [archive, setArchive] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 540);

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 540);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (currencies.length > 0) {
      setSelectedItem({ type: "currency", ...currencies[0] });
    } else if (coins.length > 0) {
      setSelectedItem({ type: "coin", ...coins[0] });
    }
  }, [currencies, coins]);

  useEffect(() => {
    const getArchive = async () => {
      if (!selectedItem) return;
      const res = await apiFetch("/api/archive", {
        method: "POST",
        body: JSON.stringify({
          type: selectedItem.type,
          id: selectedItem.id,
          limit: 40,
        }),
      });

      const resData = await res.json();
      setArchive(resData.archive?.slice?.()?.reverse() ?? []);
    };

    getArchive();
  }, [selectedItem]);

  const chartData = useMemo(() => {
    if (archive.length === 0) return null;

    const type = selectedItem?.type;
    const id = selectedItem?.id;

    const currentItem =
      type === "currency"
        ? currencies.find((c) => c.id === id)
        : coins.find((c) => c.id === id);

    return {
      labels: archive.map((item) =>
        moment(item.createdAt).format("HH:mm jMM/jDD")
      ),
      datasets: [
        {
          label: "نرخ فروش",
          data: archive.map((item) => item.sellPrice),
          borderColor: "#A87F00",
          backgroundColor: "#E4A700",
          borderWidth: isSmallScreen ? 0.5 : 2,
          pointRadius: isSmallScreen ? 3 : 6,
          pointHoverRadius: isSmallScreen ? 4 : 8,
          pointBackgroundColor: "#A87F00",
          pointBorderColor: "#FFF",
          tension: 0.4,
          fill: true,
        },
      ],
    };
  }, [archive, selectedItem, currencies, coins, isSmallScreen]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        titleFont: {
          size: 14,
        },
        bodyFont: {
          size: 12,
        },
        padding: 10,
        callbacks: {
          label: (context) => `قیمت: ${context.raw}`,
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: isSmallScreen ? 8 :12,
          },
          color: "#888",
        },
      },
      y: {
        grid: {
          color: "#EEE",
        },
        ticks: {
          font: {
            size: isSmallScreen ? 8 :12,
          },
          color: "#888",
          callback: (value) => `${value.toLocaleString()} تومان`,
        },
      },
    },
  };

  const handleChange = (e) => {
    const [type, id] = e.target.value.split("-");
    const newSelectedItem =
      type === "currency"
        ? { type, ...currencies.find((currency) => currency.id == id) }
        : { type, ...coins.find((coin) => coin.id == id) };
    setSelectedItem(newSelectedItem);
  };

  if (!selectedItem) {
    return <div>در حال بارگذاری...</div>;
  }

  return (
    <div className="chart-container wide-page-margin" id="charts">
      <div className="chart-title">نمودار نرخ ارز و سکه</div>
      <div className="mx-[2.5rem] ">
        <select
          value={`${selectedItem.type}-${selectedItem.id}`}
          onChange={handleChange}
          style={{ marginBottom: "20px", padding: "8px", borderRadius: "4px" }}
        >
          <option value="" disabled>
            انتخاب کنید
          </option>
          {currencies.map((currency) => (
            <option key={currency.id} value={`currency-${currency.id}`}>
              {currency?.currency?.name}
            </option>
          ))}
          {coins.map((coin) => (
            <option key={coin.id} value={`coin-${coin.id}`}>
              {coin?.coin?.name}
            </option>
          ))}
        </select>
        {archive.length > 0 ? (
          <div className="chart-line-wrapper">
            <Line data={chartData} options={chartOptions} />
          </div>
        ) : (
          <div style={{ textAlign: "center", fontSize: "14px", color: "#666" }}>
            داده‌ای برای نمایش وجود ندارد
          </div>
        )}
      </div>
    </div>
  );
};

export default Chart;
